import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
    Target, Calendar, Brain, Image as ImageIcon, Send, BarChart3,
    Sparkles, Hash, Palette, Type, ArrowRight, Instagram, Linkedin, Twitter,
    Music2, MessageCircle, Facebook, Eye, Heart, TrendingUp
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const ACCENT = "var(--mod-accent)";
const DEEP = "var(--mod-accent-deep)";

/**
 * Cinematic Workflow scene for TotalKlickADS landing.
 * Mostra in 6 step animati come una richiesta brief diventa un calendario social pubblicato.
 *
 * Step:
 *   01 Brand DNA caricato        → cards palette/tono/target
 *   02 Brief richiesta            → input testo che "si scrive da solo"
 *   03 AI Copy generato           → 6 post per piattaforma in cascata
 *   04 AI Image brand-coded       → 4 grafiche generate
 *   05 Calendar editoriale        → drag su giorni
 *   06 Auto-publish & Analytics   → impressions/engagement
 */
export default function ADSWorkflowScene() {
    const containerRef = useRef(null);
    return (
        <section
            ref={containerRef}
            className="relative px-5 md:px-12 py-24 md:py-32"
            data-testid="ads-workflow-scene"
        >
            <div className="max-w-6xl mx-auto">
                <Header />
                <div className="mt-16 space-y-24 md:space-y-32">
                    <Step01BrandDNA />
                    <Step02Brief />
                    <Step03Copy />
                    <Step04Image />
                    <Step05Calendar />
                    <Step06Publish />
                </div>
            </div>
        </section>
    );
}

function Header() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <div ref={ref} className="text-center">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: EASE }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                style={{ background: "var(--mod-accent-soft)", border: `1px solid ${ACCENT}` }}
            >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT, boxShadow: `0 0 8px var(--mod-accent-glow)` }} />
                <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: ACCENT, fontFamily: "JetBrains Mono, monospace" }}>
                    il workflow · 6 step · 1 cockpit
                </span>
            </motion.div>
            <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1, duration: 1, ease: EASE }}
                className="text-white tracking-tight"
                style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 5vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
            >
                Da una <span style={{ fontWeight: 600 }}>idea</span><br />
                a un calendario social <span style={{ fontWeight: 600 }}>pubblicato</span>.
            </motion.h2>
            <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2, duration: 1, ease: EASE }}
                className="mt-5 text-white/55 mx-auto"
                style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(0.95rem, 1.5vw, 1.1rem)", lineHeight: 1.55, maxWidth: "34em" }}
            >
                Scorri. Ogni passo è una cosa che <b className="text-white/80">non devi più fare tu</b>.
            </motion.p>
        </div>
    );
}

// ----- Step wrapper -----
function StepBlock({ n, title, sub, side = "right", children }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-150px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: EASE }}
            className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${side === "left" ? "lg:[direction:rtl]" : ""}`}
        >
            <div className={`lg:[direction:ltr] ${side === "left" ? "lg:order-2" : ""}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: ACCENT, fontFamily: "JetBrains Mono, monospace" }}>
                    {n}
                </div>
                <h3
                    className="text-white tracking-tight mb-3"
                    style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(1.7rem, 3.5vw, 2.8rem)", lineHeight: 1.05, letterSpacing: "-0.025em" }}
                >
                    {title}
                </h3>
                <p className="text-white/60 text-[15px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif", maxWidth: "26em" }}>
                    {sub}
                </p>
            </div>
            <div className="lg:[direction:ltr]">{children}</div>
        </motion.div>
    );
}

function OSFrame({ children, label = "ads-studio" }) {
    return (
        <div
            className="relative rounded-2xl overflow-hidden"
            style={{
                background: "rgba(15,18,36,0.65)",
                border: "1px solid rgba(255,255,255,0.07)",
                backdropFilter: "blur(20px) saturate(140%)",
                WebkitBackdropFilter: "blur(20px) saturate(140%)",
                boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
            }}
        >
            <div className="flex items-center justify-between px-3.5 py-2 border-b border-white/5">
                <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,95,87,0.5)" }} />
                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,189,46,0.5)" }} />
                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(39,201,63,0.5)" }} />
                </div>
                <span className="text-[9px] uppercase tracking-[0.18em] text-white/35" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {label}
                </span>
            </div>
            <div className="p-4 md:p-5">{children}</div>
        </div>
    );
}

// ===== STEP 01 — Brand DNA =====
function Step01BrandDNA() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    const palette = ["#7C3AED", "#FF1A5C", "#FBBF24", "#10B981"];
    return (
        <StepBlock
            n="01"
            title={<>Carichi il <span style={{ fontWeight: 600 }}>Brand DNA</span> una sola volta.</>}
            sub="Logo, palette, tono di voce, target, hashtag, esempi, parole vietate. Da questo momento l'AI conosce il tuo cliente come ci lavorasse da anni."
        >
            <div ref={ref}>
                <OSFrame label="brand-dna · acme coffee · 78% completo">
                    <div className="space-y-3.5">
                        {/* Palette */}
                        <div>
                            <Tag icon={<Palette size={11} />}>Palette</Tag>
                            <div className="flex gap-1.5 mt-2">
                                {palette.map((c, i) => (
                                    <motion.div
                                        key={c}
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={inView ? { scale: 1, opacity: 1 } : {}}
                                        transition={{ delay: 0.3 + i * 0.08, duration: 0.5, ease: EASE }}
                                        className="w-10 h-10 rounded-lg"
                                        style={{ background: c, boxShadow: `0 6px 16px -4px ${c}66` }}
                                    />
                                ))}
                            </div>
                        </div>
                        {/* Tone */}
                        <div>
                            <Tag icon={<Type size={11} />}>Tono di voce</Tag>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={inView ? { opacity: 1 } : {}}
                                transition={{ delay: 0.75, duration: 0.6 }}
                                className="mt-2 px-3 py-2 rounded-lg text-[13px] text-white/85"
                                style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.3)", fontFamily: "Outfit, sans-serif" }}
                            >
                                Playful · Conversazionale · Ironico · Mai corporate
                            </motion.div>
                        </div>
                        {/* Target */}
                        <div>
                            <Tag icon={<Target size={11} />}>Target</Tag>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={inView ? { opacity: 1 } : {}}
                                transition={{ delay: 0.95, duration: 0.6 }}
                                className="mt-2 text-[13px] text-white/70"
                                style={{ fontFamily: "Outfit, sans-serif" }}
                            >
                                Uomini 25-40, amanti del specialty coffee, urbani, attenti al rituale.
                            </motion.div>
                        </div>
                        {/* Hashtag preferiti */}
                        <div>
                            <Tag icon={<Hash size={11} />}>Hashtag preferiti</Tag>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {["#coffee", "#specialty", "#italianbarista", "#sidamo"].map((h, i) => (
                                    <motion.span
                                        key={h}
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={inView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ delay: 1.15 + i * 0.07, duration: 0.5 }}
                                        className="px-2 py-0.5 rounded-full text-[11px]"
                                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.75)", fontFamily: "JetBrains Mono, monospace" }}
                                    >
                                        {h}
                                    </motion.span>
                                ))}
                            </div>
                        </div>
                        {/* Progress */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={inView ? { opacity: 1 } : {}}
                            transition={{ delay: 1.5, duration: 0.5 }}
                            className="flex items-center gap-2.5 pt-1"
                        >
                            <div className="flex-1 h-1 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={inView ? { width: "78%" } : {}}
                                    transition={{ delay: 1.6, duration: 1.5, ease: EASE }}
                                    className="h-full rounded-full"
                                    style={{ background: `linear-gradient(90deg, ${ACCENT}, ${DEEP})` }}
                                />
                            </div>
                            <span className="text-[11px]" style={{ color: ACCENT, fontFamily: "JetBrains Mono, monospace" }}>78%</span>
                        </motion.div>
                    </div>
                </OSFrame>
            </div>
        </StepBlock>
    );
}

function Tag({ icon, children }) {
    return (
        <div className="inline-flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.18em] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            {icon} {children}
        </div>
    );
}

// ===== STEP 02 — Brief =====
function Step02Brief() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-150px" });
    const fullBrief = "Lancio del nuovo blend Etiopia Sidamo, edizione limitata 200 confezioni, prezzo 18€.";
    const [typed, setTyped] = useState("");

    useEffect(() => {
        if (!inView) return;
        let i = 0;
        const t = setInterval(() => {
            i++;
            setTyped(fullBrief.slice(0, i));
            if (i >= fullBrief.length) clearInterval(t);
        }, 35);
        return () => clearInterval(t);
    }, [inView]);

    return (
        <StepBlock
            n="02"
            side="left"
            title={<>Scrivi un brief <span style={{ fontWeight: 600 }}>in 10 secondi</span>.</>}
            sub="Niente prompt complicati. Una frase normale, come la diresti al tuo collaboratore al telefono."
        >
            <div ref={ref}>
                <OSFrame label="brief · acme coffee">
                    <div className="space-y-2.5">
                        <Tag icon={<Brain size={11} />}>Brief utente</Tag>
                        <div
                            className="px-3.5 py-3 rounded-xl text-[14px] min-h-[80px]"
                            style={{
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid var(--mod-accent)",
                                color: "#fff",
                                fontFamily: "Outfit, sans-serif",
                                boxShadow: "0 0 0 3px var(--mod-accent-soft)",
                            }}
                        >
                            {typed}
                            <span className="inline-block w-[2px] h-[15px] align-middle ml-0.5" style={{ background: ACCENT, animation: "blink 1s infinite" }} />
                        </div>
                        <div className="flex items-center gap-2 mt-2.5">
                            <Tag icon={<Sparkles size={11} />}>Piattaforme target</Tag>
                            <div className="flex gap-1.5">
                                {["IG", "FB", "LK", "X", "TT", "TH"].map((p, i) => (
                                    <motion.span
                                        key={p}
                                        initial={{ scale: 0 }}
                                        animate={inView ? { scale: 1 } : {}}
                                        transition={{ delay: 2.4 + i * 0.07, duration: 0.4, ease: EASE }}
                                        className="px-2 py-0.5 rounded text-[10px] font-bold"
                                        style={{
                                            background: "var(--mod-accent-soft)",
                                            color: ACCENT,
                                            border: "1px solid var(--mod-accent)",
                                            fontFamily: "JetBrains Mono, monospace",
                                        }}
                                    >
                                        {p}
                                    </motion.span>
                                ))}
                            </div>
                        </div>
                        <motion.button
                            initial={{ opacity: 0, y: 8 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 2.9, duration: 0.6 }}
                            type="button"
                            className="w-full mt-2 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-white text-[13px]"
                            style={{
                                background: `linear-gradient(135deg, ${ACCENT}, ${DEEP})`,
                                fontFamily: "Geist, sans-serif",
                                boxShadow: "0 10px 30px -8px var(--mod-accent-glow)",
                            }}
                        >
                            <Sparkles size={13} /> Genera 6 post
                        </motion.button>
                    </div>
                </OSFrame>
                <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>
            </div>
        </StepBlock>
    );
}

// ===== STEP 03 — AI Copy =====
const PLATFORMS = [
    { id: "ig", name: "Instagram", icon: Instagram, color: "#E1306C", copy: "☕ Sidamo è in tostatura. 200 confezioni. Solo questa settimana." },
    { id: "li", name: "LinkedIn", icon: Linkedin, color: "#0077B5", copy: "Quando un caffè può raccontare una storia: presentiamo il blend Etiopia Sidamo." },
    { id: "x", name: "X", icon: Twitter, color: "#1DA1F2", copy: "Sidamo. 200 confezioni. 18€. Solo questa settimana." },
    { id: "tt", name: "TikTok", icon: Music2, color: "#FE2C55", copy: "POV: il tuo barista preferito ha appena trovato il blend dell'anno." },
    { id: "fb", name: "Facebook", icon: Facebook, color: "#1877F2", copy: "Cari amici del bar Acme, una piccola grande novità in arrivo questa settimana." },
    { id: "th", name: "Threads", icon: MessageCircle, color: "#9B59B6", copy: "Stiamo provando un blend etiope nuovo. È diverso. Vi dico solo questo." },
];

function Step03Copy() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <StepBlock
            n="03"
            title={<>L&apos;AI scrive <span style={{ fontWeight: 600 }}>6 post diversi</span>.</>}
            sub="Ogni piattaforma riceve il SUO tono. Hook breve per X, racconto editoriale per LinkedIn, hook visivo per Instagram, gen-z per TikTok. Mai un copia-incolla."
        >
            <div ref={ref} className="grid grid-cols-2 gap-2">
                {PLATFORMS.map((p, i) => (
                    <motion.div
                        key={p.id}
                        initial={{ opacity: 0, y: 30, scale: 0.95 }}
                        animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                        transition={{ delay: i * 0.12, duration: 0.7, ease: EASE }}
                        className="p-3 rounded-xl"
                        style={{
                            background: "rgba(15,18,36,0.6)",
                            border: "1px solid rgba(255,255,255,0.07)",
                            backdropFilter: "blur(14px)",
                            WebkitBackdropFilter: "blur(14px)",
                        }}
                    >
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <p.icon size={12} style={{ color: p.color }} />
                            <span className="text-[10px] uppercase tracking-[0.16em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {p.name}
                            </span>
                        </div>
                        <p className="text-[11.5px] text-white/85 leading-snug line-clamp-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {p.copy}
                        </p>
                    </motion.div>
                ))}
            </div>
        </StepBlock>
    );
}

// ===== STEP 04 — AI Image =====
function Step04Image() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    const formats = [
        { label: "Square 1:1", aspect: "aspect-square" },
        { label: "Portrait 4:5", aspect: "aspect-[4/5]" },
        { label: "Story 9:16", aspect: "aspect-[9/16]" },
        { label: "Landscape 16:9", aspect: "aspect-[16/9]" },
    ];
    return (
        <StepBlock
            n="04"
            side="left"
            title={<>E genera <span style={{ fontWeight: 600 }}>4 grafiche brand-coded</span>.</>}
            sub="Gemini Nano Banana rispetta palette, tono visivo, target. Stessa identità su square, portrait, story e landscape. Zero stock photos."
        >
            <div ref={ref} className="grid grid-cols-4 gap-2">
                {formats.map((f, i) => (
                    <motion.div
                        key={f.label}
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: i * 0.15, duration: 0.7, ease: EASE }}
                        className={`relative rounded-lg overflow-hidden ${f.aspect}`}
                        style={{
                            background: `linear-gradient(135deg, ${["#7C3AED", "#FF1A5C", "#FBBF24", "#10B981"][i]} 0%, rgba(20,18,36,0.9) 100%)`,
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={inView ? { opacity: 1 } : {}}
                            transition={{ delay: 0.4 + i * 0.15, duration: 0.6 }}
                            className="absolute inset-0 grid place-items-center"
                        >
                            <ImageIcon size={20} className="text-white/40" />
                        </motion.div>
                        <div className="absolute bottom-1 left-1 text-[8px] uppercase tracking-wider text-white/50" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            {f.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </StepBlock>
    );
}

// ===== STEP 05 — Calendar =====
function Step05Calendar() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    const days = Array.from({ length: 28 }, (_, i) => i + 1);
    const postsOn = { 3: 1, 7: 2, 10: 1, 14: 3, 18: 1, 21: 2, 25: 1 };
    return (
        <StepBlock
            n="05"
            title={<>Si <span style={{ fontWeight: 600 }}>distribuiscono</span> da soli sul calendario.</>}
            sub="Drag&drop il post sul giorno. Eventi italiani precaricati (festività, San Valentino, Black Friday). L'AI propone post per i giorni vuoti."
        >
            <div ref={ref}>
                <OSFrame label="calendario editoriale · ottobre 2026">
                    <div className="grid grid-cols-7 gap-1.5">
                        {["L", "M", "M", "G", "V", "S", "D"].map((d) => (
                            <div key={d} className="text-center text-[9px] uppercase tracking-wider text-white/40 pb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>{d}</div>
                        ))}
                        {days.map((d, i) => (
                            <motion.div
                                key={d}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={inView ? { opacity: 1, scale: 1 } : {}}
                                transition={{ delay: i * 0.015, duration: 0.4 }}
                                className="aspect-square rounded-md p-1 relative"
                                style={{
                                    background: postsOn[d] ? "var(--mod-accent-soft)" : "rgba(255,255,255,0.03)",
                                    border: postsOn[d] ? "1px solid var(--mod-accent)" : "1px solid rgba(255,255,255,0.05)",
                                }}
                            >
                                <div className="text-[10px] text-white/70" style={{ fontFamily: "JetBrains Mono, monospace" }}>{d}</div>
                                {postsOn[d] && (
                                    <div className="absolute bottom-1 left-1 right-1 flex gap-0.5">
                                        {Array.from({ length: postsOn[d] }).map((_, k) => (
                                            <div key={k} className="flex-1 h-0.5 rounded-full" style={{ background: ACCENT }} />
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </OSFrame>
            </div>
        </StepBlock>
    );
}

// ===== STEP 06 — Publish + Analytics =====
function Step06Publish() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    const channels = [
        { icon: Instagram, color: "#E1306C", label: "IG" },
        { icon: Facebook, color: "#1877F2", label: "FB" },
        { icon: Linkedin, color: "#0077B5", label: "LK" },
        { icon: Twitter, color: "#1DA1F2", label: "X" },
        { icon: Music2, color: "#FE2C55", label: "TT" },
    ];
    return (
        <StepBlock
            n="06"
            side="left"
            title={<>Pubblicano. <span style={{ fontWeight: 600 }}>Misurano. Imparano.</span></>}
            sub="Auto-publish su 5 social. Analytics in tempo reale. L'AI ti dice qual è il post che ha funzionato meglio e perché — e te ne suggerisce di simili."
        >
            <div ref={ref}>
                <OSFrame label="auto-publish · live analytics">
                    <div className="space-y-3">
                        {/* Channels broadcast */}
                        <div className="flex items-center gap-3 justify-center py-2">
                            <div
                                className="w-9 h-9 rounded-xl grid place-items-center"
                                style={{
                                    background: `linear-gradient(135deg, ${ACCENT}, ${DEEP})`,
                                    boxShadow: "0 8px 24px -6px var(--mod-accent-glow)",
                                }}
                            >
                                <Send size={16} className="text-white" />
                            </div>
                            <ArrowRight size={14} className="text-white/30" />
                            <div className="flex gap-1.5">
                                {channels.map((c, i) => (
                                    <motion.div
                                        key={c.label}
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={inView ? { scale: 1, opacity: 1 } : {}}
                                        transition={{ delay: 0.3 + i * 0.1, duration: 0.5, ease: EASE }}
                                        className="w-8 h-8 rounded-lg grid place-items-center"
                                        style={{ background: `${c.color}22`, border: `1px solid ${c.color}55` }}
                                    >
                                        <c.icon size={14} style={{ color: c.color }} />
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5">
                            <Stat icon={Eye} label="Impressions" value="14.2K" delay={0.9} inView={inView} />
                            <Stat icon={Heart} label="Engagement" value="+18%" delay={1.05} inView={inView} accent />
                            <Stat icon={TrendingUp} label="Click" value="362" delay={1.2} inView={inView} />
                        </div>
                        {/* AI insight */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 1.5, duration: 0.7 }}
                            className="flex items-start gap-2 px-3 py-2 rounded-lg"
                            style={{ background: "var(--mod-accent-soft)", border: "1px solid var(--mod-accent)" }}
                        >
                            <Sparkles size={13} className="mt-0.5 flex-none" style={{ color: ACCENT }} />
                            <p className="text-[11.5px] text-white/75 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Il post Instagram di martedì ha avuto <b className="text-white">+260% engagement</b>. Rifallo con un altro prodotto?
                            </p>
                        </motion.div>
                    </div>
                </OSFrame>
            </div>
        </StepBlock>
    );
}

function Stat({ icon: Icon, label, value, delay, inView, accent }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay, duration: 0.6 }}
            className="text-center px-2"
        >
            <Icon size={12} className={accent ? "" : "text-white/40"} style={accent ? { color: ACCENT } : null} />
            <div className="text-white mt-1" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500, fontSize: "17px" }}>
                {value}
            </div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-white/40 mt-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {label}
            </div>
        </motion.div>
    );
}
